import * as Yup from "yup";
import {
  requiredString,
  emailField,
  // emailFieldRequired,
  // passwordField,
  passwordFieldRequired,
  phoneNumberField,
  phoneNumberFieldRequired,
  // postalCodeField,
  postalCodeFieldRequired,
  requiredFile,
  aadharNumberFieldRequired,
} from "./validationRules";

export const personalDetailsValidationSchema = Yup.object().shape({
  // ── Required fields ──────────────────────────────────────────────────
  fieldOfficerId:          requiredString("Field Officer"),
  name:                    requiredString("Name"),
  gender:                  requiredString("Gender"),
  dob:                     requiredString("Date of Birth"),
  age: Yup.number()
    .typeError("Age is required")
    .min(0, "Age must be at least 0")
    .required("Age is required"),
  mobileNumber:            phoneNumberFieldRequired("Mobile Number"),
  maritalStatus:           requiredString("Marital Status"),
  permanentStreetAddress:  requiredString("Permanent Street Address"),
  permanentPostalCode:     postalCodeFieldRequired(),

  // ── Required on UI, not saved in personal_details table ───────────────
  aadharNo:                Yup.string().required("Aadhar is required"),

  // ── Optional fields ───────────────────────────────────────────────────
  permanentCity:           Yup.string().nullable().notRequired(),
  permanentState:          Yup.string().nullable().notRequired(),
  permanentDistrict:       Yup.string().nullable().notRequired(),
  permanentVillage:        Yup.string().nullable().notRequired(),
  currentStreetAddress:    Yup.string().nullable().notRequired(),
  currentCity:             Yup.string().nullable().notRequired(),
  currentState:            Yup.string().nullable().notRequired(),
  currentDistrict:         Yup.string().nullable().notRequired(),
  currentPostalCode:       Yup.string().nullable().notRequired(),
  currentVillage:          Yup.string().nullable().notRequired(),
  citizenship:             Yup.string().nullable().notRequired(),
  // Required only when CREATING a customer on the Customers screen.
  //
  // The loan wizard shows these read-only and shares this schema, so an
  // unconditional required() would block a loan nobody can unblock. And 10 of
  // the 12 existing customers hold neither image — requiring them on Edit would
  // make every one of those records uneditable, so correcting a phone number
  // would first demand two uploads.
  //
  // `$isAdd` comes from the form's context (PersonalDetails.js), so the same
  // schema enforces creation and stays quiet everywhere else.
  customerProfileImage: Yup.mixed()
    .nullable()
    .when("$isAdd", {
      is: true,
      then: (s) => s.required("Customer Profile Image is required"),
      otherwise: (s) => s.notRequired(),
    }),
  customerSignatureImage: Yup.mixed()
    .nullable()
    .when("$isAdd", {
      is: true,
      then: (s) => s.required("Customer Signature Image is required"),
      otherwise: (s) => s.notRequired(),
    }),
  altMobileNumber:         phoneNumberField("Alt Mobile Number"),
  fatherName:              Yup.string().nullable().notRequired(),
  spouseName:              Yup.string().nullable().notRequired(),
  permanentDoorNo:         Yup.string().nullable().notRequired(),
  currentDoorNumber:       Yup.string().nullable().notRequired(),
  sameAsPermanentAddress:  Yup.boolean().notRequired(),
});

export const verificationDetailsValidationSchema = Yup.object().shape({
  aadharNo: aadharNumberFieldRequired("Aadhar No"),
});

export const branchDetailsValidationSchema = Yup.object().shape({
  branchName: requiredString("Branch Name"),
  country: requiredString("Country"),
  state: requiredString("State"),
  district: requiredString("District"),
  address: requiredString("Address"),
  pincode: postalCodeFieldRequired("Pincode"),
  village: requiredString("Village"),
});

export const centerDetailsValidationSchema = Yup.object().shape({
  branchId: requiredString("Branch Name"),
  centerName: requiredString("Center Name"),
  address: requiredString("Address"),
  pincode: postalCodeFieldRequired("Pincode"),
  phoneNo: phoneNumberField("Phone No"),
  landMark: requiredString("Land Mark"),
});

export const roleDetailsValidationSchema = Yup.object().shape({
  roleName: requiredString("Role Name"),
});

export const staffDetailsValidationSchema = Yup.object().shape({
  staffImage: requiredFile("Staff Image"),
  name: requiredString("Name"),
  phoneNo: phoneNumberFieldRequired("Phone No"),
  emailId: emailField("Email"),
  username: requiredString("Username"),
  password: passwordFieldRequired("Password"),
  dob: requiredString("Date of Birth"),
  doj: requiredString("Date of Joining"),
  gender: requiredString("Gender"),
  designation: requiredString("Designation"),
  address: requiredString("Address"),
  state: requiredString("State"),
  city: requiredString("City"),
  roleId: requiredString("Role"),
  branchId: requiredString("Branch"),
});

export const accountDetailsValidationSchema = Yup.object().shape({
  customerUniqueId: requiredString("Customer Unique ID"),
  accountType: requiredString("Account Type"),
});

export const depositsValidationSchema = Yup.object().shape({
  accountType: requiredString("Account Type"),
  accountNumber: requiredString("Account Number"),
  amount: requiredString("Amount"),
});

export const memberDetailsValidationSchema = Yup.object().shape({
  residenceType: requiredString("Residence Type"),
  stayingStabilityInYears: requiredString("Staying Stability in Years"),
  stayingStabilityInMonths: requiredString("Staying Stability in Months"),
});

// DD.MM.YYYY — the only format adminLoanController.parseEuropeanDate accepts.
const DOT_DATE = /^\d{2}\.\d{2}\.\d{4}$/;

/**
 * Proposed Loan Details — the four values an EMI schedule cannot be built without.
 *
 * These fields carried `required: true` in utils.js, which drew a red asterisk and
 * enforced nothing: FormInput calls register(name) with no rules, and this form had
 * no resolver, so handleSubmit always succeeded. A loan therefore saved with
 * emiDate = NULL.
 *
 * What that costs: adminLoanController.js:180 refuses to build a chart when
 * emiDate, rateOfInterest or a positive tenureInMonths is missing, so the loan
 * stays 'pending' with no loanstatement rows — while the disbursement path checks
 * only the funding agency and releases the money anyway. Loan JLBL00000002 went out
 * at Rs.95,640 with no schedule behind it.
 *
 * All four are validated, not just the date: the production diagnostic
 * (scripts/diag_missing_chart.js) reports EMIDATE_MISSING 18, TENURE_0 2 and
 * TENURE_NULL 1, so every one of these is a route to the same failure.
 */
// The three funding ROWS as the form draws them (utils.js:2781-2817).
//
// The amounts are numbered one out of step with the agencies — row 1 is
// fundingAgencyId with fundingAmount2, row 2 is fundingAgencyId2 with
// fundingAmount1. There is no fundingAgencyId1. Pairing them by name would put
// the operator's money against the wrong agency, so the rows are written out
// once here and everything else reads from this list.
// Exported so the disbursement modal maps over this same list rather than
// keeping its own copy. It used to hardcode the labels "Federal"/"Own"/"Other"
// beside the amount boxes — copied from here, WITHOUT the pairing and in the
// wrong order, so the first box read "Federal" while row 1 is Own Fund. The
// result contradicted itself on screen ("Funding Amount Federal (OWN FUND)").
//
// `label` is for validation messages. The modal shows the SELECTED agency's
// name instead, so no fixed word can disagree with the dropdown.
export const FUNDING_ROWS = [
  { agency: "fundingAgencyId",  amount: "fundingAmount2", label: "Own Fund" },
  { agency: "fundingAgencyId2", amount: "fundingAmount1", label: "Federal Fund" },
  { agency: "fundingAgencyId3", amount: "fundingAmount3", label: "Other Fund" },
];

const numOrZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const hasAgency = (v) =>
  v !== null && v !== undefined && v !== "" && Number(v) > 0;

const fundingTotalOf = (parent) =>
  FUNDING_ROWS.reduce((sum, row) => sum + numOrZero(parent[row.amount]), 0);

// Each amount box carries three checks. The first is per-ROW, so the message
// lands on the box the operator must actually fix; the other two are about the
// funding as a whole.
//
//   1. row paired      — an agency chosen means THAT row's amount must be filled,
//                        and an amount filled means THAT row needs an agency
//   2. funding present  — at least one row funded; blocks the "jams" case where a
//                        Rs.1,00,000 loan was saved with every amount 0
//   3. funding reconciles — the rows must add up to the loan amount
//
// A rupee of tolerance absorbs float noise; the columns are INTEGER, so a real
// mismatch is always at least 1.
const fundingAmountRule = (rowIndex) => {
  const row = FUNDING_ROWS[rowIndex];

  return Yup.number()
    .transform((value, original) =>
      original === "" || original === null ? 0 : value
    )
    .typeError(`${row.label} must be a number`)
    .min(0, `${row.label} cannot be negative`)
    .test(
      "funding-row-paired",
      `${row.label} and its Funding Agency must be filled together.`,
      function () {
        const agencyChosen = hasAgency(this.parent[row.agency]);
        const amountEntered = numOrZero(this.parent[row.amount]) > 0;

        if (agencyChosen && !amountEntered) {
          return this.createError({
            message: `A Funding Agency is selected on this row, so enter its ${row.label} amount.`,
          });
        }
        if (amountEntered && !agencyChosen) {
          return this.createError({
            message: `${row.label} has an amount, so select the Funding Agency for this row.`,
          });
        }
        return true;
      }
    )
    .test(
      "funding-present",
      "Select a funding agency and enter its amount — a loan cannot be saved without a funding source.",
      function () {
        return fundingTotalOf(this.parent) > 0;
      }
    )
    .test(
      "funding-reconciles",
      // A function-form message is NOT used here: yup resolves it before the test
      // runs, so it cannot see the computed figures and renders an empty string.
      // createError below carries the numbers instead.
      "Funding must add up to the Loan Amount.",
      function () {
        const total = fundingTotalOf(this.parent);
        const loan = Number(this.parent.loanAmount);
        // loanAmount has its own required/positive rules; don't double-report.
        if (!Number.isFinite(loan) || loan <= 0) return true;
        if (total <= 0) return true;   // the rule above owns this case
        if (Math.abs(total - loan) < 1) return true;

        const short = loan - total;
        return this.createError({
          message:
            `Funding must add up to the Loan Amount. Entered ` +
            `${total.toLocaleString("en-IN")} against a loan of ` +
            `${loan.toLocaleString("en-IN")} — ` +
            (short > 0
              ? `${short.toLocaleString("en-IN")} short.`
              : `${Math.abs(short).toLocaleString("en-IN")} over.`),
        });
      }
    );
};

export const proposedLoanDetailsValidationSchema = Yup.object().shape({
  applicantName: requiredString("Applicant Name"),
  emiDate: Yup.string()
    .required("Loan Date is required")
    .matches(DOT_DATE, "Loan Date must be DD.MM.YYYY"),
  loanAmount: Yup.number()
    .typeError("Loan Amount must be a number")
    .positive("Loan Amount must be greater than zero")
    .required("Loan Amount is required"),
  rateOfInterest: requiredString("Interest Rate"),
  tenureInMonths: Yup.number()
    .typeError("Tenure must be a number")
    .integer("Tenure must be a whole number")
    .positive("Tenure must be greater than zero")
    .required("Tenure in Months is required"),

  // Funding must be stated, and must add up to the loan.
  //
  // A loan saved with no funding amounts cannot be attributed to a source when it
  // is disbursed: postLoanDisbursement falls back to charging the FIRST agency for
  // the whole amount, so an agency that funded nothing carries the debt. Live
  // holds 853 such loans. Member "jams" was one — Rs.1,00,000 sanctioned with
  // fundingAmount1 and fundingAmount2 both 0.
  //
  // Indexed into FUNDING_ROWS so each box knows which Funding Agency dropdown
  // sits on its own row — see the note there about the off-by-one numbering.
  fundingAmount2: fundingAmountRule(0),   // row 1 — Own Fund
  fundingAmount1: fundingAmountRule(1),   // row 2 — Federal Fund
  fundingAmount3: fundingAmountRule(2),   // row 3 — Other Fund
});

export const memberVerificationDetailsValidationSchema = Yup.object().shape({
  aadharNo: aadharNumberFieldRequired("Aadhar No"),
  aadharFrontImage: requiredFile("Aadhar Front Image"),
  aadharBackImage: requiredFile("Aadhar Back Image"),
  panNo: requiredString("PAN No"),
  idProof: requiredString("ID Proof"),
  idProofNo: requiredString("ID Proof No"),
  idProofFrontImage: requiredFile("ID Proof Front Image"),
  idProofBackImage: requiredFile("ID Proof Back Image"),
  addressProof: requiredString("Address Proof"),
  addressProofNo: requiredString("Address Proof No"),
  addressProofFrontImage: requiredFile("Address Proof Front Image"),
  addressProofBackImage: requiredFile("Address Proof Back Image"),
});

export const memberNomineeDetailsValidationSchema = Yup.object().shape({
  name: requiredString("Nominee Name"),
  fatherName: requiredString("Father Name"),
  gender: requiredString("Gender"),
  dob: requiredString("Date of Birth"),
  mobileNumber: phoneNumberFieldRequired("Mobile Number"),
  relationship: requiredString("Relationship"),
  address: requiredString("Address"),
  aadharNo: aadharNumberFieldRequired("Aadhar No"),
  idProof: requiredString("ID Proof"),
  idProofNo: requiredString("Id Proof No"),
  idProofFrontImage: requiredFile("ID Proof Front Image"),
  idProofBackImage: requiredFile("ID Proof Back Image"),
});

export const memberBankDetailsValidationSchema = Yup.object().shape({
  accountNumber: requiredString("Account Number"),
  accountHolderName: requiredString("Account Holder Name"),
  ifscCode: requiredString("IFSC Code"),
  bankName: requiredString("Bank Name"),
  bankBranchName: requiredString("Bank Branch Name"),
  accountType: requiredString("Account Type"),
});

export const fundingAgencyValidationSchema = Yup.object().shape({
  agencyName: requiredString("Agency Name"),
});
